import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { Alert } from '@mui/material';
import * as Sentry from '@sentry/browser';

import {
  HeaderRow,
  SubTitle,
  Title,
  TitleContainer,
} from '../../../../../components/v2/Header';
import ExternalCoachContext, {
  withCoachProductsContextReady,
  withExternalCoachContextReady,
} from '../../../../context/ExternalCoachContext';
import { Card, CardsContainer } from '../../../../../components/v2/Card';
import useSessionStore from '../../../../../hooks/useSessionStore';
import Package from '../../../../../Model/Package';
import Form from '../../../../Model/Form';
import LabelCheckbox from '../../../../components/LabelCheckbox';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useToast from '../../../../hooks/useToast';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import {
  ButtonContainer,
  StyledAddIcon,
  StyledButton,
  StyledAlert,
} from './styles';
import AddProductModal from './components/AddProductModal';
import ProductView from './components/ProductView';
import texts from './texts.json';
import FreeProductSignUpLink from './components/FreeProductSignUpLink';
import InternalConfigs from './components/InternalConfigs';
import OneTimeContracts from './components/OneTimeContracts';
import InsideSalesOfferings from './components/InsideSalesOfferings';

const ProductsConfig = () => {
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [productCollection, setProductCollection] = useState({ docs: [] });
  const [coachPPFForms, setCoachPPFForms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categorizedPackagesCol, setCategorizedPackagesCol] = useState({ docs: [] });

  const coachPPFFormExists = coachPPFForms.length > 0;

  const { externalCoachDoc, coachDoc } = useContext(ExternalCoachContext);

  // Missing config if base fee is missing. A missing PPF (no native form and no Paperform
  // link) is surfaced separately as a non-blocking admin warning below — it no longer hides
  // the add-product button or the product list, since products can be created PPF-less.
  const isBaseFeeConfigMissing = (!externalCoachDoc.baseFee && !coachDoc.feesConfig.baseFee?.value);
  const isConfigMissing = isBaseFeeConfigMissing;
  const isPPFConfigMissing = !coachPPFFormExists && !externalCoachDoc.postPaymentFormLink;

  const { isAdmin } = useSessionStore();
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  // Hide default products ,hidden products & archived products
  const filteredProducts = productCollection.docs.filter((product) => !product.isDefault && !product.isHidden
    && (showArchived || !product.isArchived));

  const categorizedPackagesWithProduct = (categorizedPackagesCol?.docs || []).map((pkg) => ({
    pkg,
    product: productCollection.docs.find((p) => p.id === pkg.productId),
  }));

  const loadCategorizedPackages = useCallback(async () => {
    if (!coachDoc?.isACEnabled) {
      return;
    }
    const pkgCol = await Package.getCategorizedPackages(coachDoc.id);
    if (isComponentMountedRef.current) {
      setCategorizedPackagesCol(pkgCol);
    }
  }, [coachDoc, isComponentMountedRef]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const [productCol, coachPPFFormCol] = await Promise.all([
          externalCoachDoc.getProducts(true),
          Form.getPinnablePostPaymentForms(coachDoc.id),
          loadCategorizedPackages(),
        ]);

        if (isComponentMountedRef.current) {
          setProductCollection(productCol);
          setCoachPPFForms(coachPPFFormCol);
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { coachId: coachDoc?.id } });
        if (isComponentMountedRef.current) {
          showToast(texts.loadError, { error: true });
        }
      } finally {
        if (isComponentMountedRef.current) {
          setIsLoading(false);
        }
      }
    };
    init();
  }, [externalCoachDoc, isComponentMountedRef, coachDoc, showToast, loadCategorizedPackages]);

  return (
    <CardsContainer>
      <Card>
        <FreeProductSignUpLink />
      </Card>
      <Card>
        <OneTimeContracts />
      </Card>
      <InsideSalesOfferings
        products={productCollection.docs}
        categorizedPackagesWithProduct={categorizedPackagesWithProduct}
        onOfferingsChanged={loadCategorizedPackages}
      />
      <Card>
        <HeaderRow>
          <TitleContainer>
            <Title>{texts.title}</Title>
            <SubTitle>{isBaseFeeConfigMissing && isAdmin ? texts.missingConfigSubTitle : texts.subTitle}</SubTitle>
          </TitleContainer>
          {!isConfigMissing && (
          <ButtonContainer>
            <LabelCheckbox
              description={texts.showArchived}
              checked={showArchived}
              onChange={() => setShowArchived(!showArchived)}
            />
            <StyledButton onClick={() => setShowAddProductModal(true)}>
              <StyledAddIcon />
              {texts.addNewProduct}
            </StyledButton>
          </ButtonContainer>
          )}
        </HeaderRow>
        {!isConfigMissing && (
        <>
          {filteredProducts.map((product) => (
            <ProductView
              product={product}
              key={product.id}
              showArchived={showArchived}
              categorizedPackagesWithProduct={categorizedPackagesWithProduct}
              coachPPFForms={coachPPFForms}
              coachPPFFormsLoaded={!isLoading}
              coachDefaultFormId={coachDoc?.defaultPostPaymentFormId || ''}
            />
          ))}
          {!filteredProducts.length && (<Alert severity="info">{texts.noProducts}</Alert>)}
        </>
        )}
        {/* show error if user is not admin */}
        {isConfigMissing && !isAdmin
        && (
          <Alert severity="error">{texts.missingConfigError}</Alert>
        )}
        {/* show warning if user is admin and there is no native PPF and no Paperform link */}
        {isPPFConfigMissing && isAdmin && (
        <StyledAlert severity="warning">{texts.missingPPF}</StyledAlert>
        )}
        {/* show configs if user is admin and coachPPFForm exists */}
        {isBaseFeeConfigMissing && isAdmin && <InternalConfigs />}
        {showAddProductModal && (
        <AddProductModal
          open={showAddProductModal}
          onClose={() => setShowAddProductModal(false)}
        />
        )}
      </Card>
      <LoadingOverlay isLoading={isLoading} />
    </CardsContainer>
  );
};

export default compose(
  withExternalCoachContextReady,
  withCoachProductsContextReady,
  observer,
)(ProductsConfig);
